/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { CustomerMinimal } from './CustomerMinimal';
import type { FileMinimal } from './FileMinimal';
import type { HazardousLocationClassification } from './HazardousLocationClassification';
import type { IntegrationInfo } from './IntegrationInfo';
import type { SensorAssociation } from './SensorAssociation';
import type { SensorInfo } from './SensorInfo';
import type { SensorSTMReading } from './SensorSTMReading';
import type { SteamState } from './SteamState';
import type { SteamTrapInstallDetail } from './SteamTrapInstallDetail';
import type { SteamTrapOperationType } from './SteamTrapOperationType';
import type { SteamTrapState } from './SteamTrapState';

/**
 * A steam trap.
 */
export type SteamTrap = {
	/**
	 * The Everactive id for the steam trap.
	 */
	id: string;
	customer?: CustomerMinimal;
	/**
	 * The timestamp (unix epoch) the steam trap was associated with the site.
	 * The timestamp is the earliest possible time series reading available for the steam trap.
	 */
	readonly customerAssociationTimestamp?: number;
	/**
	 * If the steam trap is associated with one or more external systems this array will have the external system integration info the client is allowed to view.
	 */
	integrationInfo?: Array<IntegrationInfo>;
	lastReading?: SensorSTMReading;
	profileImage?: FileMinimal;
	/**
	 * The sensor currently installed on the steam trap.
	 */
	sensor?: {
		id: string;
		lastAssociation?: SensorAssociation;
		lastInfo?: SensorInfo;
		lastReading?: SensorSTMReading;
		macAddress: string;
		/**
		 * The firmware version of the sensor at manufacturing time
		 */
		manufacturedFirmwareVersion?: string;
		/**
		 * The product name for the sensor
		 */
		productName?: string;
		/**
		 * Flag indicating the sensor requires service to provide accurate readings.
		 */
		serviceNeeded?: boolean;
		status?: string;
		type?: string;
	};
	/**
	 * A flag indicating the condensate and steam temperature probes were swapped when installed.
	 * This flag is used to correct the mapping of temperatures so the values are returned
	 * correctly from the API when retrieving time series data for an STM sensor.
	 */
	sensorInstallTempProbesSwapped?: boolean;
	/**
	 * The timestamp (unix epoch) the current Eversensor was installed on the steam trap.
	 */
	sensorInstallTimestamp?: number;
	/**
	 * Flag indicating whether Unexpected Cold event detection is suppressed for this steam trap.
	 */
	suppressUnexpectedCold?: boolean;
	/**
	 * The user id that last set `suppressUnexpectedCold` to true. Cleared when suppression is disabled.
	 */
	readonly suppressedBy?: string | null;
	/**
	 * The timestamp when `suppressUnexpectedCold` was last set to true. Cleared when suppression is disabled.
	 */
	readonly suppressedAt?: string | null;
	/**
	 * The reason `suppressUnexpectedCold` was last set to true. Cleared when suppression is disabled.
	 */
	suppressionReason?: SteamTrap.suppressionReason | null;
	/**
	 * Details about the physical location and environment of the steam trap.
	 */
	siteDetail: {
		/**
		 * Short description of how the steam trap is used.
		 */
		application?: string;
		/**
		 * Detailed description of how the steam trap is used.
		 */
		applicationDetail?: string;
		/**
		 * The facility where the steam trap is located.
		 */
		facility: string;
		hazardousLocationClassification?: HazardousLocationClassification;
		/**
		 * The inlet PSI measurement collected during the site survey.
		 */
		inletPSI?: number;
		installDetail?: SteamTrapInstallDetail;
		/**
		 * The location of the steam trap within the facility.
		 */
		location: string;
		operation?: SteamTrapOperationType;
		/**
		 * The diameter of the steam trap pipe in inches.
		 */
		pipeSize?: number;
		surveyData?: {
			/**
			 * A historical property used to identify the Evergateway the steam trap sensor will report through.
			 * @deprecated
			 */
			controlNodeNumber?: number;
			/**
			 * Typically the Evergateway serial number the steam trap sensor will report through.
			 */
			evergatewayAssociationNumber?: string;
			/**
			 * Notes collected when the site survey was conducted that may help in sensor installation.
			 */
			notes?: string;
		};
		/**
		 * The service tag of the steam trap.
		 */
		svcTag?: string;
	};
	/**
	 * The current state of the steam trap and the steam in the steam trap.
	 */
	status?: {
		/**
		 * The total dollar loss amount for the period(s) of time the trap was in a failure state.
		 */
		amountLost?: number;
		steamState?: SteamState;
		/**
		 * The timestamp the steam state last changed.
		 */
		steamStateChangeTimestamp?: number;
		/**
		 * The timestamp when the current failure started. Only populated when the trap is in a failure state.
		 */
		trapFailureTimestamp?: number;
		trapState?: SteamTrapState;
		/**
		 * The timestamp of when the current steam trap state was set.
		 */
		trapStateTimestamp?: number;
	};
	/**
	 * Property details of the trap itself.
	 */
	trapDetail: {
		/**
		 * The manufacturer of the steam trap.
		 */
		manufacturer: string;
		/**
		 * The model of the steam trap.
		 */
		model?: string;
		/**
		 * The orifice diameter of the steam trap in inches.
		 */
		orificeDiameter?: number;
		/**
		 * The type of steam trap
		 */
		type?: string;
	};
};

export namespace SteamTrap {

	/**
	 * The reason `suppressUnexpectedCold` was last set to true. Cleared when suppression is disabled.
	 */
	export enum suppressionReason {
		OUTDOOR_TRAP = 'outdoor_trap',
		SEASONAL_TRAP = 'seasonal_trap',
		CUSTOMER_REQUEST = 'customer_request',
		TESTING = 'testing',
	}


}

